import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { bookApi, categoryApi } from '../services/api';

function BookList() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // 构建查询参数
      const params = categoryId ? { category_id: categoryId } : {};
      
      const booksData = await bookApi.getBooks(params);
      
      setBooks(booksData);
      
      // 如果有分类ID，获取分类详情
      if (categoryId) {
        try {
          const categoryData = await categoryApi.getCategory(categoryId);
          setCategory(categoryData);
        } catch (err) {
          console.error('获取分类详情失败:', err);
        }
      } else {
        setCategory(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleScrape = async (e, bookId) => {
    e.stopPropagation(); // 阻止事件冒泡
    try {
      await bookApi.startScrape(bookId);
      alert('抓取任务已启动！');
    } catch (err) {
      alert('启动抓取失败: ' + err.message);
    }
  };

  const handleBookClick = (bookId) => {
    navigate(`/books/${bookId}/read`);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: '待抓取', class: 'bg-slate-100 text-slate-700 border-slate-200' },
      scraping: { text: '抓取中', class: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
      completed: { text: '已完成', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      failed: { text: '失败', class: 'bg-rose-50 text-rose-700 border-rose-200' },
    };
    const config = statusMap[status] || statusMap.pending;
    return (
      <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${config.class}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="rounded-2xl border border-slate-200 bg-white px-8 py-6 text-slate-700 shadow-sm">
          正在加载书籍列表...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-rose-200 bg-rose-50 p-6">
        <p className="text-rose-700">加载失败: {error}</p>
        <button 
          onClick={loadData}
          className="mt-4 rounded-lg bg-rose-600 px-4 py-2 text-white transition hover:bg-rose-700"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center text-sm text-slate-500">
        <Link to="/" className="transition hover:text-indigo-600">首页</Link>
        <span className="mx-2">/</span>
        {category ? (
          <>
            <Link to="/" className="transition hover:text-indigo-600">分类</Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-slate-800">{category.name}</span>
          </>
        ) : (
          <span className="font-medium text-slate-800">全部书籍</span>
        )}
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">
          {category ? `${category.name}书籍` : '全部书籍'}
        </h1>
        {category?.description && (
          <p className="mt-2 text-sm text-slate-600">{category.description}</p>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-900">
            {category ? `${category.name} (${books.length}本)` : `书籍列表 (${books.length}本)`}
          </h2>
          <div className="flex items-center space-x-2">
            <Link 
              to="/"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-50"
            >
              返回首页
            </Link>
          </div>
        </div>

        {books.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            暂无书籍
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {books.map((book) => (
              <div 
                key={book.id} 
                onClick={() => handleBookClick(book.id)}
                className="group cursor-pointer p-5 transition hover:bg-slate-50"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 group-hover:text-indigo-700">
                      {book.title}
                    </h3>
                    {book.author && (
                      <p className="mt-1 text-sm text-slate-600">作者: {book.author}</p>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                      <span>
                        章节: {book.scraped_chapters}/{book.total_chapters}
                      </span>
                      <span>
                        进度: {book.total_chapters > 0 
                          ? Math.round((book.scraped_chapters / book.total_chapters) * 100)
                          : 0}%
                      </span>
                      {getStatusBadge(book.status)}
                    </div>
                    {book.description && (
                      <p className="mt-3 line-clamp-2 text-sm text-slate-600">
                        {book.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 self-center">
                    {book.status === 'pending' && book.scraped_chapters < book.total_chapters && (
                      <button
                        onClick={(e) => handleScrape(e, book.id)}
                        className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white transition hover:bg-emerald-700"
                      >
                        抓取
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default BookList;
